/**
 * lifecyclePresentation — ONE presentation of an analysis lifecycle for every
 * surface that renders it.
 *
 * The Findings matrix and Path to GO showed DIFFERENT tags for the SAME
 * analysis (live, Redhead 2026-08-10): a held-but-answerable BTM Feasibility
 * read amber "Held — 5 statements disagree with the site evidence" in
 * Findings, and red "FAILED" in Path to GO. Same data, opposite instructions —
 * one says "answer the questions", the other says "the run broke, retry",
 * and retrying against unchanged evidence just reproduces the finding.
 *
 * The divergence existed because each view kept its OWN label map and its own
 * (or no) answerable-softening rule: 8d6c2d53a taught FindingsDossier that
 * `failed` + answerable truth questions ⇒ HELD, and Path to GO never got the
 * same lesson. Sharing the module — not duplicating the rule — is the fix
 * that stays fixed.
 *
 * The lifecycle VALUE is never changed here. Data stays `failed`; consumers
 * keying off it (gating, retries, telemetry) are untouched. Only what the
 * reader is told changes.
 */
import type { SiteRagReport } from '../../types/siteRag';
import { filterUnsettledTruthQuestions } from '../audit/truthAttestations';
import type { TruthQuestion } from '../audit/qaSeverityPolicy';

/** Superset of ReportLifecycle plus the FindingsDossier-local extensions. */
export type PresentableLifecycle =
  | 'needs-input'
  | 'dependency-blocked'
  | 'ready'
  | 'queued'
  | 'generating'
  | 'preliminary'
  | 'preview'
  | 'current'
  | 'refresh-needed'
  | 'quality-held'
  | 'failed'
  | 'failed-coverage'
  | 'unavailable'
  | 'available'
  | 'missing';

export type LifecycleTone = 'success' | 'info' | 'warn' | 'danger' | 'muted';

export interface LifecyclePresentation {
  /**
   * The badge text, or null when the lifecycle carries NO on-screen badge.
   * `quality-held` has none (owner, 2026-09-30): an amber "Quality review"
   * pill sat beside the analysis's own green "QA · pass" chip on the same
   * Analyses row and read as a contradiction. The state itself is untouched —
   * gating, ordering and the gap lane still key off it.
   */
  label: string | null;
  tone: LifecycleTone;
  /** True when a `failed` lifecycle was softened to HELD (answerable). */
  held: boolean;
}

/**
 * Canonical labels. Wording follows the Findings matrix (the richer surface);
 * Path to GO previously said "Quality held" / "Analyzing" for the same states,
 * which read as two different systems to anyone comparing tabs.
 */
const LABEL: Record<PresentableLifecycle, string | null> = {
  'needs-input': 'Needs evidence',
  'dependency-blocked': 'Setup needed',
  ready: 'Ready to analyse',
  queued: 'Queued',
  generating: 'Analysing',
  preliminary: 'Preliminary',
  preview: 'Preview only',
  current: 'Current',
  'refresh-needed': 'Refresh required',
  'quality-held': null,
  failed: 'Failed',
  'failed-coverage': 'Coverage blocked',
  unavailable: 'Unavailable',
  available: 'Available',
  missing: 'Missing',
};

const TONE: Record<PresentableLifecycle, LifecycleTone> = {
  current: 'success',
  ready: 'info',
  queued: 'info',
  generating: 'info',
  preliminary: 'info',
  preview: 'info',
  available: 'info',
  'needs-input': 'warn',
  'dependency-blocked': 'warn',
  'refresh-needed': 'warn',
  // A quality hold is a HOLD awaiting judgement, not a malfunction — warn, not
  // danger. (Path to GO previously painted it red alongside `failed`.)
  'quality-held': 'warn',
  missing: 'warn',
  failed: 'danger',
  'failed-coverage': 'danger',
  unavailable: 'danger',
};

/**
 * How many ANSWERABLE truth questions an analysis carries. A held analysis
 * whose blocking findings are all answerable questions is not a failure — the
 * report generated and is readable; it is waiting on a human, not on a retry.
 */
export function truthQuestionCountOf(analysis: SiteRagReport | null | undefined): number {
  if (!analysis) return 0;
  if (analysis.qualityHold !== 'needs-revision' && analysis.qualityReview?.verdict !== 'needs-revision') {
    return 0;
  }
  const qaObj = (analysis as { qaFindings?: Record<string, unknown> } | null | undefined)?.qaFindings;
  const qa = qaObj?.truthQuestions;
  if (!Array.isArray(qa)) return 0;
  return filterUnsettledTruthQuestions(
    qa as TruthQuestion[],
    undefined,
    analysis.reportType,
    undefined,
    undefined,
    undefined,
    qaObj,
  ).length;
}

/**
 * Resolve the presentation for a lifecycle.
 *
 * `failed` + answerable ⇒ "Held" in the warning tone: the user's move is to
 * answer the questions, and presenting it as a red failure sends them to a
 * Retry that regenerates against unchanged evidence and reproduces the block.
 */
export function lifecyclePresentation(
  lifecycle: PresentableLifecycle,
  truthQuestionCount: number,
  /**
   * The auto-rebind cap has given up on this domain
   * (`pointer.autoRebindExhaustedAt`, finalizeReportRevision
   * AUTO_REBIND_MAX_CONSECUTIVE). A bare "Refresh required" then tells the
   * reader to wait for a refresh that will never arrive on its own — the same
   * two-surfaces-disagree problem this module exists to prevent, since Findings
   * already explains the give-up in its error block.
   *
   * Scoped to `refresh-needed` deliberately: the flag is sticky (only the next
   * commit clears it), so a queued/generating row must keep saying it is
   * running, and a `current` analysis is fine regardless.
   */
  autoRefreshExhausted = false,
): LifecyclePresentation {
  if (lifecycle === 'failed' && truthQuestionCount > 0) {
    return { label: 'Held', tone: 'warn', held: true };
  }
  if (lifecycle === 'refresh-needed' && autoRefreshExhausted) {
    return { label: 'Manual rerun needed', tone: 'warn', held: false };
  }
  return { label: LABEL[lifecycle], tone: TONE[lifecycle], held: false };
}
